import axios, { isAxiosError } from "axios";
import { Cookies } from "react-cookie";
import { env } from "@monta/env";
import { ServerLogger } from "./logger";

import type {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig
} from "axios";
import type { Auth } from "./auth";
import type { ExtendedAxiosConfig } from "./http-factory-types";

/**
 * HTTP Client Configuration Options
 */
export interface HttpClientConfig extends Partial<InternalAxiosRequestConfig> {
  baseURL: string;
  timeout?: number;
  retryAttempts?: number;
  retryDelay?: number;
  enableLogging?: boolean;
  customHeaders?: Record<string, string>;
  authHeaderKey?: string;
  contentType?: string;
}

/**
 * HTTP Factory Options
 */
export interface HttpFactoryOptions {
  auth: Auth;
  enableGlobalErrorHandling?: boolean;
  enableRequestLogging?: boolean;
  enableResponseLogging?: boolean;
  onUnauthorized?: () => void;
  onForbidden?: () => void;
  onNetworkError?: (error: AxiosError) => void;
}

/**
 * Request/Response Logging Interface
 */
interface RequestLog {
  method: string;
  url: string;
  timestamp: string;
  headers?: Record<string, unknown>;
  data?: unknown;
}

interface ResponseLog extends RequestLog {
  status: number;
  statusText: string;
  duration: number;
  responseData?: unknown;
}

/**
 * HTTP Error Types
 */
export enum HttpErrorType {
  NETWORK_ERROR = "NETWORK_ERROR",
  TIMEOUT_ERROR = "TIMEOUT_ERROR",
  UNAUTHORIZED = "UNAUTHORIZED",
  FORBIDDEN = "FORBIDDEN",
  NOT_FOUND = "NOT_FOUND",
  SERVER_ERROR = "SERVER_ERROR",
  UNKNOWN_ERROR = "UNKNOWN_ERROR"
}

/**
 * Custom HTTP Error Class
 */
export class EnhancedHttpError extends Error {
  constructor(
    public type: HttpErrorType,
    public status?: number,
    public originalError?: AxiosError,
    message?: string
  ) {
    super(message || `HTTP Error: ${type}`);
    this.name = "EnhancedHttpError";
  }
}

/**
 * HTTP Client Wrapper with Enhanced Features
 */
export class EnhancedHttpClient {
  private readonly client: AxiosInstance;
  private readonly config: HttpClientConfig;
  private readonly factoryOptions: HttpFactoryOptions;
  private readonly requestQueue: Map<string, AbortController> = new Map();

  constructor(config: HttpClientConfig, factoryOptions: HttpFactoryOptions) {
    this.config = config;
    this.factoryOptions = factoryOptions;
    this.client = this.createClient();
    this.setupInterceptors();
  }

  /**
   * Create axios client with base configuration
   */
  private createClient(): AxiosInstance {
    const client = axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout || 30000,
      headers: {
        "Content-Type": this.config.contentType || "application/json",
        ...this.config.customHeaders
      }
    });

    return client;
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => this.handleRequest(config),
      (error) => this.handleRequestError(error)
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => this.handleResponse(response),
      (error) => this.handleResponseError(error)
    );
  }

  /**
   * Handle outgoing requests
   */
  private handleRequest(
    config: InternalAxiosRequestConfig
  ): InternalAxiosRequestConfig {
    const requestId = this.generateRequestId();
    const controller = new AbortController();

    // Add abort signal
    config.signal = controller.signal;
    (config as ExtendedAxiosConfig).metadata = {
      requestId,
      startTime: Date.now()
    };

    // Store controller for potential cancellation
    this.requestQueue.set(requestId, controller);

    // Add authentication token
    const token = new Cookies().get("accessToken");
    if (token && this.config.authHeaderKey) {
      config.headers.set(this.config.authHeaderKey, token);
    }

    // Log request if enabled
    if (this.factoryOptions.enableRequestLogging) {
      this.logRequest(config, requestId);
    }

    return config;
  }

  /**
   * Handle request errors
   */
  private handleRequestError(error: unknown): Promise<never> {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";
    ServerLogger.error(
      `Request setup failed: ${errorMessage}`,
      "HTTP_REQUEST_ERROR"
    );
    return Promise.reject(
      new EnhancedHttpError(
        HttpErrorType.UNKNOWN_ERROR,
        undefined,
        error as AxiosError
      )
    );
  }

  /**
   * Handle incoming responses
   */
  private handleResponse(response: AxiosResponse): AxiosResponse {
    const { requestId, startTime } =
      (response.config as ExtendedAxiosConfig).metadata || {};

    // Clean up request tracking
    if (requestId) {
      this.requestQueue.delete(requestId);
    }

    // Log response if enabled
    if (this.factoryOptions.enableResponseLogging && requestId) {
      this.logResponse(response, requestId, startTime);
    }

    return response;
  }

  /**
   * Handle response errors with comprehensive error handling
   */
  private async handleResponseError(error: AxiosError): Promise<never> {
    const { requestId } = (error.config as ExtendedAxiosConfig)?.metadata || {};

    // Clean up request tracking
    if (requestId) {
      this.requestQueue.delete(requestId);
    }

    // Log error
    this.logError(error, requestId);

    // Handle specific error types
    if (!isAxiosError(error)) {
      return Promise.reject(
        new EnhancedHttpError(HttpErrorType.UNKNOWN_ERROR, undefined, error)
      );
    }

    const status = error.response?.status;

    switch (status) {
      case 401:
        return this.handleUnauthorizedError(error);
      case 403:
        return this.handleForbiddenError(error);
      case 404:
        return Promise.reject(
          new EnhancedHttpError(HttpErrorType.NOT_FOUND, status, error)
        );
      case 500:
      case 502:
      case 503:
      case 504:
        return this.handleServerError(error);
      default:
        if (error.code === "ECONNABORTED") {
          return Promise.reject(
            new EnhancedHttpError(HttpErrorType.TIMEOUT_ERROR, status, error)
          );
        }
        if (error.code === "ERR_NETWORK") {
          return Promise.reject(
            new EnhancedHttpError(HttpErrorType.NETWORK_ERROR, status, error)
          );
        }
        return Promise.reject(
          new EnhancedHttpError(HttpErrorType.UNKNOWN_ERROR, status, error)
        );
    }
  }

  /**
   * Handle 401 Unauthorized errors with token refresh
   */
  private async handleUnauthorizedError(error: AxiosError): Promise<never> {
    if (error.response?.status === 401) {
      this.factoryOptions.auth.logout();
      this.factoryOptions.onUnauthorized?.();
      return Promise.reject(
        new EnhancedHttpError(HttpErrorType.UNAUTHORIZED, 401, error)
      );
    }

    return Promise.reject(
      new EnhancedHttpError(HttpErrorType.UNAUTHORIZED, 401, error)
    );
  }

  /**
   * Handle 403 Forbidden errors
   */
  private handleForbiddenError(error: AxiosError): Promise<never> {
    ServerLogger.warning("Access forbidden", "AUTH_FORBIDDEN");
    this.factoryOptions.onForbidden?.();
    return Promise.reject(
      new EnhancedHttpError(HttpErrorType.FORBIDDEN, 403, error)
    );
  }

  /**
   * Handle server errors with retry logic
   */
  private async handleServerError(error: AxiosError): Promise<never> {
    const retryAttempts = this.config.retryAttempts || 3;
    const retryDelay = this.config.retryDelay || 1000;
    const currentAttempt =
      (error.config as ExtendedAxiosConfig)?.metadata?.retryAttempt || 0;

    if (currentAttempt < retryAttempts) {
      ServerLogger.warning(
        `Server error, retrying... (${currentAttempt + 1}/${retryAttempts})`,
        "HTTP_RETRY"
      );

      // Wait before retry
      await new Promise((resolve) =>
        setTimeout(resolve, retryDelay * (currentAttempt + 1))
      );

      // Update retry attempt
      const originalConfig = error.config! as ExtendedAxiosConfig;
      originalConfig.metadata = {
        requestId: originalConfig.metadata?.requestId || "",
        startTime: originalConfig.metadata?.startTime || Date.now(),
        retryAttempt: currentAttempt + 1
      };

      return this.client(originalConfig);
    }

    return Promise.reject(
      new EnhancedHttpError(
        HttpErrorType.SERVER_ERROR,
        error.response?.status,
        error
      )
    );
  }

  /**
   * Generate unique request ID
   */
  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Log outgoing requests
   */
  private logRequest(
    config: InternalAxiosRequestConfig,
    requestId: string
  ): void {
    const requestLog: RequestLog = {
      method: config.method?.toUpperCase() || "UNKNOWN",
      url: `${config.baseURL}${config.url}`,
      timestamp: new Date().toISOString(),
      headers: config.headers.toJSON(),
      data: config.data
    };

    ServerLogger.info(
      `[${requestId}] ${requestLog.method} ${requestLog.url}`,
      "HTTP_REQUEST"
    );

    if (this.config.enableLogging) {
      console.log("📤 Request Details:", requestLog);
    }
  }

  /**
   * Log incoming responses
   */
  private logResponse(
    response: AxiosResponse,
    requestId: string,
    startTime?: number
  ): void {
    const duration = startTime ? Date.now() - startTime : 0;

    const responseLog: ResponseLog = {
      method: response.config.method?.toUpperCase() || "UNKNOWN",
      url: `${response.config.baseURL}${response.config.url}`,
      timestamp: new Date().toISOString(),
      status: response.status,
      statusText: response.statusText,
      duration,
      responseData: response.data
    };

    ServerLogger.success(
      `[${requestId}] ${responseLog.status} ${responseLog.statusText} (${duration}ms)`,
      "HTTP_RESPONSE"
    );

    if (this.config.enableLogging) {
      console.log("📥 Response Details:", responseLog);
    }
  }

  /**
   * Log errors
   */
  private logError(error: AxiosError, requestId?: string): void {
    const status = error.response?.status || "UNKNOWN";
    const message = error.message || "Unknown error";
    const url = error.config?.url || "Unknown URL";

    ServerLogger.error(
      `[${requestId || "unknown"}] ${status} - ${message} - ${url}`,
      "HTTP_ERROR"
    );

    if (this.config.enableLogging) {
      console.error("❌ Error Details:", {
        status,
        message,
        url,
        response: error.response?.data,
        stack: error.stack
      });
    }
  }

  /**
   * Cancel all pending requests
   */
  public cancelAllRequests(): void {
    this.requestQueue.forEach((controller, requestId) => {
      controller.abort();
      ServerLogger.info(`Cancelled request: ${requestId}`, "HTTP_CANCEL");
    });
    this.requestQueue.clear();
  }

  /**
   * Cancel specific request
   */
  public cancelRequest(requestId: string): void {
    const controller = this.requestQueue.get(requestId);
    if (controller) {
      controller.abort();
      this.requestQueue.delete(requestId);
      ServerLogger.info(`Cancelled request: ${requestId}`, "HTTP_CANCEL");
    }
  }

  /**
   * Get the underlying axios instance
   */
  public getClient(): AxiosInstance {
    return this.client;
  }

  /**
   * Update client configuration
   */
  public updateConfig(newConfig: Partial<HttpClientConfig>): void {
    Object.assign(this.config, newConfig);

    // Update axios defaults
    if (newConfig.timeout) {
      this.client.defaults.timeout = newConfig.timeout;
    }
    if (newConfig.customHeaders) {
      Object.assign(
        this.client.defaults.headers.common,
        newConfig.customHeaders
      );
    }
  }
}

/**
 * Enhanced HTTP Factory
 */
export class EnhancedHttpFactory {
  private readonly auth: Auth;
  private options: HttpFactoryOptions;

  constructor(auth: Auth, options: Partial<HttpFactoryOptions> = {}) {
    this.auth = auth;
    this.options = {
      enableGlobalErrorHandling: true,
      enableRequestLogging: true,
      enableResponseLogging: true,
      ...options,
      auth
    };
  }

  /**
   * Create Monolithic API Client
   */
  public createMonolithicClient(
    config?: Partial<HttpClientConfig>
  ): EnhancedHttpClient {
    const clientConfig: HttpClientConfig = {
      baseURL: `${env.API_URL}/monolithic`,
      timeout: 30000,
      retryAttempts: 3,
      retryDelay: 1000,
      enableLogging: process.env.NODE_ENV === "development",
      authHeaderKey: "Authorization",
      contentType: "application/json",
      ...config
    };

    return new EnhancedHttpClient(clientConfig, this.options);
  }

  /**
   * Create GWT API Client
   */
  public createGwtClient(
    config?: Partial<HttpClientConfig>
  ): EnhancedHttpClient {
    const clientConfig: HttpClientConfig = {
      baseURL: env.API_URL,
      timeout: 30000,
      retryAttempts: 3,
      retryDelay: 1000,
      enableLogging: process.env.NODE_ENV === "development",
      authHeaderKey: "Authorization",
      contentType: !config?.fetchOptions?.["isJson"]
        ? "application/x-www-form-urlencoded; charset=UTF-8"
        : "application/json",
      ...config
    };

    return new EnhancedHttpClient(clientConfig, this.options);
  }

  /**
   * Create Node API Client
   */
  public createNodeClient(
    config?: Partial<HttpClientConfig>
  ): EnhancedHttpClient {
    const clientConfig: HttpClientConfig = {
      baseURL: `${env.LEGACY_APPLICATION_URL}/api`,
      timeout: 30000,
      retryAttempts: 3,
      retryDelay: 1000,
      enableLogging: process.env.NODE_ENV === "development",
      authHeaderKey: "Authorization",
      contentType: "application/json",
      customHeaders: {
        Accept: "application/pdf"
      },
      ...config
    };

    return new EnhancedHttpClient(clientConfig, this.options);
  }

  /**
   * Create custom HTTP client
   */
  public createCustomClient(config: HttpClientConfig): EnhancedHttpClient {
    return new EnhancedHttpClient(config, this.options);
  }

  /**
   * Update factory options
   */
  public updateOptions(newOptions: Partial<HttpFactoryOptions>): void {
    this.options = { ...this.options, ...newOptions };
  }
}

/**
 * Factory function to create Enhanced HTTP Factory
 */
export function createHttpFactory(
  auth: Auth,
  options?: Partial<HttpFactoryOptions>
): EnhancedHttpFactory {
  return new EnhancedHttpFactory(auth, options);
}

/**
 * Export types
 */
export type HttpError = EnhancedHttpError;
